import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { ApiError } from "../../api/client";
import { HierarchyApi } from "../hierarchy/api";
import { RbacApi, type Assignment } from "./api";

type Level = Assignment["scopeLevel"];

interface Prefill {
  // Carries existing values into the form when the user is replacing an
  // assignment. The optional parentCid / parentWid let us load wards and
  // branches for ward- and branch-scoped existing assignments so the
  // dropdowns aren't empty when the form opens.
  roleId: string;
  positionId: string;
  scopeLevel: Level;
  scopeId: string;
  reportsToUid: string;
  endsAt: string;
  parentCid?: string;
  parentWid?: string;
}

/**
 * Per-user assignment manager. Lists current assignments for one user and
 * provides an inline form to add new ones — or replace an existing one in
 * place. Used by the Users page to fold what was previously the standalone
 * Assignments page into the user record.
 *
 * "Replace" is a client-side composite: it deletes the old assignment after
 * the new one is successfully created, so there is no read window where the
 * user has neither.
 */
export function AssignmentManager({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [replacingId, setReplacingId] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<Prefill | null>(null);

  const users = useQuery({ queryKey: ["users"], queryFn: RbacApi.listUsers });
  const roles = useQuery({ queryKey: ["roles"], queryFn: RbacApi.listRoles });
  const positions = useQuery({ queryKey: ["positions"], queryFn: RbacApi.listPositions });
  const constituencies = useQuery({
    queryKey: ["constituencies"],
    queryFn: HierarchyApi.listConstituencies,
  });
  const assignments = useQuery({
    queryKey: ["assignments", userId],
    queryFn: () => RbacApi.listAssignments(userId),
  });

  const close = () => { setAdding(false); setReplacingId(null); setPrefill(null); };

  const create = useMutation({
    mutationFn: RbacApi.createAssignment,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assignments", userId] });
      qc.invalidateQueries({ queryKey: ["users"] });
      close();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });
  const remove = useMutation({
    mutationFn: RbacApi.deleteAssignment,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assignments", userId] });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  // Replace = create the new assignment first; only delete the old one if
  // the create succeeded. This keeps the user accessable for the entire op.
  const replace = useMutation({
    mutationFn: async (vars: { newBody: Omit<Assignment, "id">; oldId: string }) => {
      await RbacApi.createAssignment(vars.newBody);
      await RbacApi.deleteAssignment(vars.oldId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assignments", userId] });
      qc.invalidateQueries({ queryKey: ["users"] });
      close();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  // Helper to look up parent IDs for an existing ward or branch assignment,
  // so the form's parent dropdowns can default to the right values.
  async function startEdit(a: Assignment) {
    setError(null);
    let parentCid: string | undefined;
    let parentWid: string | undefined;
    try {
      if (a.scopeLevel === "ward") {
        const wards = await Promise.all(
          (constituencies.data?.items ?? []).map(async (c) => {
            const list = await HierarchyApi.listWards(c.id);
            return { cid: c.id, wardIds: list.items.map((w) => w.id) };
          }),
        );
        parentCid = wards.find((w) => w.wardIds.includes(a.scopeId))?.cid;
      } else if (a.scopeLevel === "branch") {
        // Find the ward containing this branch, and the constituency containing that ward.
        for (const c of constituencies.data?.items ?? []) {
          const wList = await HierarchyApi.listWards(c.id);
          for (const w of wList.items) {
            const bList = await HierarchyApi.listBranches(w.id);
            if (bList.items.find((b) => b.id === a.scopeId)) {
              parentCid = c.id;
              parentWid = w.id;
              break;
            }
          }
          if (parentCid) break;
        }
      }
    } catch {
      // Best-effort prefill — if we fail, the form opens with the right scope
      // level but empty parent dropdowns; the user can re-pick.
    }
    // Set prefill BEFORE replacingId. The form is gated on replacingId, so
    // setting prefill first guarantees the form reads the populated prefill
    // on the render where it first appears. Setting replacingId first would
    // mount the form against a null prefill, baking empty defaults into its
    // local state.
    setPrefill({
      roleId: a.roleId,
      positionId: a.positionId ?? "",
      scopeLevel: a.scopeLevel,
      scopeId: a.scopeId,
      reportsToUid: a.reportsToUid ?? "",
      endsAt: a.endsAt ?? "",
      parentCid,
      parentWid,
    });
    setAdding(false);
    setReplacingId(a.id);
  }

  return (
    <div className="children">
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Roles + scopes attached to this user. Add as many as needed — effective permissions are the union of all of them.
      </div>
      {error && <div role="alert" className="error">{error}</div>}

      {assignments.isLoading && <div className="muted" style={{ fontSize: 12 }}>Loading…</div>}
      {assignments.data?.items.length === 0 && !adding && !replacingId && (
        <div className="muted" style={{ fontSize: 13, padding: "8px 0" }}>
          No assignments yet — add one below.
        </div>
      )}

      {(assignments.data?.items ?? []).length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, marginBottom: 12 }}>
          {assignments.data?.items.map((a) => (
            <li key={a.id} className="row" style={{ marginBottom: 6 }}>
              <span style={{ fontSize: 13 }}>
                Role <strong>{a.roleId}</strong> · {a.scopeLevel} <code>{a.scopeId}</code>
                {a.positionId && <> · Position <strong>{a.positionId}</strong></>}
                {a.reportsToUid && <> · Reports to <code>{a.reportsToUid}</code></>}
                {(a.startsAt || a.endsAt) && (
                  <div className="muted" style={{ fontSize: 11 }}>
                    {a.startsAt ?? "—"} → {a.endsAt ?? "indefinite"}
                  </div>
                )}
              </span>
              <span className="row-actions">
                <button
                  className="ghost"
                  onClick={() => startEdit(a)}
                  disabled={replacingId === a.id}
                >
                  {replacingId === a.id ? "Editing…" : "Edit"}
                </button>
                <button
                  className="danger"
                  onClick={() => {
                    if (window.confirm("Revoke this assignment? The user will lose this role + scope immediately. The audit log keeps the record."))
                      remove.mutate(a.id);
                  }}
                >Revoke</button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {!adding && !replacingId && (
        <button onClick={() => { setAdding(true); setPrefill(null); }}>+ Add another role + scope</button>
      )}

      {(adding || replacingId) && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            {replacingId ? "Edit this assignment" : "Add another role + scope"}
          </div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            {replacingId
              ? "We save your changes by adding the new assignment first, then revoking the old one — the user keeps access throughout."
              : "This is in addition to any assignments above. Each new entry stacks on top — the user's effective permissions are the union of all of them."}
          </div>
          <NewAssignmentForm
            key={replacingId ?? "new"}
            userId={userId}
            users={(users.data?.items ?? []).filter((u) => u.id !== userId)}
            roles={roles.data?.items ?? []}
            positions={positions.data?.items ?? []}
            constituencies={constituencies.data?.items ?? []}
            prefill={prefill}
            onSubmit={(body) => {
              if (replacingId) replace.mutate({ newBody: body, oldId: replacingId });
              else create.mutate(body);
            }}
            onCancel={close}
            submitting={create.isPending || replace.isPending}
            submitLabel={replacingId ? "Save changes" : "Add"}
          />
        </div>
      )}
    </div>
  );
}

function NewAssignmentForm({
  userId,
  users,
  roles,
  positions,
  constituencies,
  prefill,
  onSubmit,
  onCancel,
  submitting,
  submitLabel,
}: {
  userId: string;
  users: { id: string; fullName: string; email: string }[];
  roles: { id: string; name: string }[];
  positions: { id: string; name: string; scopeLevel: Level }[];
  constituencies: { id: string; name: string }[];
  prefill: Prefill | null;
  onSubmit: (body: Omit<Assignment, "id">) => void;
  onCancel: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  const [roleId, setRoleId] = useState(prefill?.roleId ?? "");
  const [positionId, setPositionId] = useState(prefill?.positionId ?? "");
  const [scopeLevel, setScopeLevel] = useState<Level>(prefill?.scopeLevel ?? "branch");
  const [scopeId, setScopeId] = useState(prefill?.scopeId ?? "");
  const [reportsToUid, setReportsToUid] = useState(prefill?.reportsToUid ?? "");
  const [endsAt, setEndsAt] = useState(prefill?.endsAt ?? "");

  const [parentCid, setParentCid] = useState(prefill?.parentCid ?? "");
  const wards = useQuery({
    queryKey: ["wards", parentCid],
    queryFn: () => HierarchyApi.listWards(parentCid),
    enabled: !!parentCid,
  });
  const [parentWid, setParentWid] = useState(prefill?.parentWid ?? "");
  const branches = useQuery({
    queryKey: ["branches", parentWid],
    queryFn: () => HierarchyApi.listBranches(parentWid),
    enabled: !!parentWid,
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!roleId || !scopeId) return;
    onSubmit({
      userId,
      roleId,
      positionId: positionId || null,
      scopeLevel,
      scopeId,
      reportsToUid: reportsToUid || null,
      startsAt: null,
      endsAt: endsAt || null,
    });
  }

  return (
    <form className="create-role" onSubmit={submit}>
      <div className="row-3">
        <label>
          Role:
          <select value={roleId} onChange={(e) => setRoleId(e.target.value)} required>
            <option value="">— choose —</option>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </label>
        <label>
          Position:
          <select value={positionId} onChange={(e) => setPositionId(e.target.value)}>
            <option value="">(none)</option>
            {positions
              .filter((p) => p.scopeLevel === scopeLevel)
              .map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label>
          Ends at:
          <input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </label>
      </div>

      <div className="row-3" style={{ marginTop: 8 }}>
        <label>
          Reports to:
          <select value={reportsToUid} onChange={(e) => setReportsToUid(e.target.value)}>
            <option value="">(no chain — top of hierarchy)</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
            ))}
          </select>
        </label>
        <span /><span />
      </div>

      <div className="row-3" style={{ marginTop: 8 }}>
        <label>
          Scope level:
          <select
            value={scopeLevel}
            onChange={(e) => {
              setScopeLevel(e.target.value as Level);
              setScopeId("");
              setParentCid("");
              setParentWid("");
            }}
          >
            <option value="constituency">Constituency</option>
            <option value="ward">Ward</option>
            <option value="branch">Branch</option>
          </select>
        </label>

        {scopeLevel === "constituency" && (
          <label>
            Constituency:
            <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} required>
              <option value="">— choose —</option>
              {constituencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        )}

        {scopeLevel === "ward" && (
          <>
            <label>
              Constituency:
              <select value={parentCid} onChange={(e) => { setParentCid(e.target.value); setScopeId(""); }} required>
                <option value="">— choose —</option>
                {constituencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label>
              Ward:
              <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} required>
                <option value="">— choose —</option>
                {wards.data?.items.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </label>
          </>
        )}

        {scopeLevel === "branch" && (
          <>
            <label>
              Constituency:
              <select value={parentCid} onChange={(e) => { setParentCid(e.target.value); setParentWid(""); setScopeId(""); }} required>
                <option value="">— choose —</option>
                {constituencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label>
              Ward:
              <select value={parentWid} onChange={(e) => { setParentWid(e.target.value); setScopeId(""); }} required>
                <option value="">— choose —</option>
                {wards.data?.items.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </label>
            <label>
              Branch:
              <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} required>
                <option value="">— choose —</option>
                {branches.data?.items.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
          </>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button type="submit" disabled={submitting}>
          {submitting ? "Saving…" : submitLabel}
        </button>
        <button type="button" className="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}
